import { Injectable } from '@angular/core';
import Handlebars from 'handlebars';
import { DateFormatService } from './date-format.service';
import { LocaleFormatService } from './locale-format.service';
import { resolveBadgeClass } from '../utils/badge-tone';

@Injectable({
  providedIn: 'root'
})
export class HandlebarsHelpersService {
  constructor(
    private dateFormatService: DateFormatService,
    private localeFormatService: LocaleFormatService
  ) {
    this.registerHelpers();
  }

  private registerHelpers() {
    // Currency formatting
    Handlebars.registerHelper('formatCurrency', (value: any, currencyCode?: any) => {
      // Handle Handlebars options object as second parameter
      let currencyCodeRaw: string | undefined;

      if (typeof currencyCode === 'string') {
        currencyCodeRaw = currencyCode;
      } else if (currencyCode && typeof currencyCode === 'object' && currencyCode.hash?.currency) {
        currencyCodeRaw = currencyCode.hash.currency;
      }

      if (value === null || value === undefined) {
        return new Handlebars.SafeString('—');
      }

      const num = Number(value);

      if (isNaN(num)) {
        return new Handlebars.SafeString('—');
      }

      const formatted = this.localeFormatService.formatCurrency(num, currencyCodeRaw, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      const escaped = Handlebars.escapeExpression(formatted);
      return new Handlebars.SafeString(`<span style="white-space: nowrap; font-variant-numeric: tabular-nums;">${escaped}</span>`);
    });

    const dateFormat = this.dateFormatService;

    // Date formatting
    /**
     * Formats a date value using the tenant/profile-aware DateFormatService.
     * Signature: {{formatDate value}} or {{formatDate value "short"|"long"|"datetime"|"calendar-date"}}
     * or {{formatDate value format="long"}}.
     * "calendar-date" preserves the stored calendar day for date-only business values.
     * Empty/null inputs always render '—'; invalid non-empty inputs render the raw value.
     */
    Handlebars.registerHelper('formatDate', function(value: any, options?: any) {
      // Extract format from second param or options.hash
      let format = 'default';
      if (typeof options === 'string') {
        format = options;
      } else if (options && options.hash && options.hash.format) {
        format = options.hash.format;
      }

      if (!value) return new Handlebars.SafeString('—');

      let result: string;
      if (format === 'long') {
        result = dateFormat.formatDateOnly(value, 'long');
      } else if (format === 'datetime') {
        result = dateFormat.formatDateTime(value, 'short');
      } else if (format === 'calendar-date') {
        result = dateFormat.formatCalendarDateOnly(value, 'short');
      } else {
        result = dateFormat.formatDateOnly(value, 'short');
      }

      if (result === '') return new Handlebars.SafeString(String(value));
      return new Handlebars.SafeString(result);
    });

    // String transformations
    Handlebars.registerHelper('uppercase', function(value: any) {
      return value ? String(value).toUpperCase() : '';
    });

    Handlebars.registerHelper('lowercase', function(value: any) {
      return value ? String(value).toLowerCase() : '';
    });

    Handlebars.registerHelper('capitalize', function(value: any) {
      if (!value) return '';
      const str = String(value);
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    });

    // Number formatting
    Handlebars.registerHelper('formatNumber', (value: any, decimals?: number) => {
      if (value === null || value === undefined || isNaN(Number(value))) {
        return '—';
      }

      const fractionDigits = typeof decimals === 'number' ? decimals : 0;
      return this.localeFormatService.formatNumber(Number(value), {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      });
    });

    // Conditional helpers
    Handlebars.registerHelper('eq', function(a: any, b: any) {
      return a === b;
    });

    Handlebars.registerHelper('ne', function(a: any, b: any) {
      return a !== b;
    });

    Handlebars.registerHelper('gt', function(a: any, b: any) {
      return Number(a) > Number(b);
    });

    Handlebars.registerHelper('lt', function(a: any, b: any) {
      return Number(a) < Number(b);
    });

    Handlebars.registerHelper('gte', function(a: any, b: any) {
      return Number(a) >= Number(b);
    });

    Handlebars.registerHelper('lte', function(a: any, b: any) {
      return Number(a) <= Number(b);
    });

    // Logical helpers
    Handlebars.registerHelper('and', function(...args: any[]) {
      // Last argument is Handlebars options, remove it
      const values = args.slice(0, -1);
      return values.every(v => !!v);
    });

    Handlebars.registerHelper('or', function(...args: any[]) {
      const values = args.slice(0, -1);
      return values.some(v => !!v);
    });

    Handlebars.registerHelper('not', function(value: any) {
      return !value;
    });

    // Default value helper
    Handlebars.registerHelper('default', function(value: any, defaultValue: any) {
      return value || defaultValue;
    });

    // Normalise database-provided badge tones before using them in a class attribute.
    Handlebars.registerHelper('badgeClass', function(value: any) {
      return resolveBadgeClass(value);
    });

    // Truncate text
    Handlebars.registerHelper('truncate', function(value: any, length: number) {
      if (!value) return '';
      const str = String(value);
      if (str.length <= length) return str;
      return str.substring(0, length) + '...';
    });
  }
}
